import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';

import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Modal,
  Row,
  Space,
} from 'antd';

import CustomCheckbox from '../../components/CustomCheckbox/CustomCheckbox';
import { ReactComponent as Picker } from '../../assets/date-picker-icon.svg';
import './style.scss';

const ModalFilterAdvise = ({
  filter,
  setFilter,
  openFilter,
  handleCloseFilter,
  onboardingForms,
  onboardingTemplates,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation('');

  const documentType = useMemo(() => {
    const pathArray = onboardingForms ? Object.keys(onboardingForms) : [];
    const getAdviceByPath = (path) => {
      const { name = '', description = '' } = onboardingTemplates?.[path] || {};
      if (name && description) {
        return {
          id: path,
          name,
          description,
        };
      }
      return null;
    };

    const data = [];
    pathArray.forEach((path) => {
      const adviceType = getAdviceByPath(path);
      if (adviceType) {
        data.push(adviceType);
      }
    });
    data.unshift({ id: 'all', description: 'ALL', name: 'ALL' });
    return data;
  }, [onboardingForms]);

  const typeList = useMemo(() => {
    const data = documentType.map((i) => [i.name, i.name === 'ALL']);
    const obj = Object.fromEntries(data);
    return obj;
  }, [documentType]);

  const initVAlue = {
    documentType: typeList,
    status: {
      ALL: true,
      COMPLETED: false,
      EDITING: false,
      AVAILABLE: false,
      SUBMITTED: false,
      PENDING: false,
      CREATED: false,
      SENT: false,
      CANCELED: false,
      ERROR: false,
    },
  };

  const [filterState, setFilterState] = useState(initVAlue);

  useEffect(() => {
    if (filter) {
      form.setFieldsValue(filter);
      setFilterState(filter);
    } else {
      form.setFieldsValue(initVAlue);
      setFilterState(initVAlue);
    }
  }, [openFilter]);

  // AVAILABLE, EDITING, SUBMITTED, PENDING, CREATED, SENT, CANCELED, COMPLETED, ERROR

  const statusList = [
    { id: 1, label: 'ALL' },
    { id: 2, label: 'AVAILABLE' },
    { id: 3, label: 'EDITING' },
    { id: 4, label: 'SUBMITTED' },
    { id: 5, label: 'PENDING' },
    { id: 6, label: 'CREATED' },
    { id: 7, label: 'SENT' },
    { id: 8, label: 'CANCELED' },
    { id: 9, label: 'COMPLETED' },
    { id: 10, label: 'ERROR' },
  ];

  const handleReset = () => {
    setFilterState(initVAlue);
    setFilter(null);
  };

  const handleCancel = () => {
    handleCloseFilter();
  };

  const handleFilterChange = (groupName, changedValue, isChecked) => {
    setFilterState((prevState) => {
      let groupValues = { ...prevState[groupName] };

      if (changedValue === 'ALL' && isChecked) {
        Object.keys(groupValues).forEach((key) => {
          groupValues[key] = key === 'ALL';
        });
      } else if (changedValue !== 'ALL' && isChecked) {
        groupValues[changedValue] = true;
        if (groupValues['ALL']) {
          groupValues['ALL'] = false;
        }

        const otherKeys = Object.keys(groupValues).filter(
          (key) => key !== 'ALL',
        );
        const allOthersChecked = otherKeys.every((key) => groupValues[key]);

        if (allOthersChecked) {
          Object.keys(groupValues).forEach((key) => {
            groupValues[key] = key === 'ALL';
          });
        }
      } else if (!isChecked) {
        groupValues[changedValue] = false;

        if (changedValue !== 'ALL') {
          const otherKeys = Object.keys(groupValues).filter(
            (key) => key !== 'ALL',
          );
          const allOthersChecked = otherKeys.every((key) => groupValues[key]);

          if (allOthersChecked) {
            Object.keys(groupValues).forEach((key) => {
              groupValues[key] = key === 'ALL';
            });
          }
        }
      }

      const hasAnyChecked = Object.values(groupValues).some((val) => val);
      if (!hasAnyChecked) {
        groupValues['ALL'] = true;
      }

      return {
        ...prevState,
        [groupName]: groupValues,
      };
    });
  };

  const handleChangeData = (data, type) => {
    setFilterState((prevState) => {
      return {
        ...prevState,
        [type]: data,
      };
    });
  };
  const onSubmit = () => {
    setFilter(filterState);
    handleCloseFilter();
  };

  return (
    <Modal
      width={710}
      className="filter-advise"
      centered
      open={openFilter}
      onCancel={handleCancel}
      footer={false}
      zIndex={1500}
    >
      <Form
        form={form}
        name="filter-advise"
        layout="vertical"
        onFinish={onSubmit}
      >
        <div>
          <Row className="profile-data-row">
            <Col>
              <div className="filter-group-title">{t('DOCUMENT_TYPE')}</div>
              <Space
                direction="horizontal"
                wrap
                style={{ rowGap: '24px', marginBottom: '24px' }}
              >
                {documentType.map((i) => (
                  <CustomCheckbox
                    width="310px"
                    key={i.id}
                    checked={filterState?.documentType?.[i.name] || false}
                    onChange={(e) =>
                      handleFilterChange(
                        'documentType',
                        i.name,
                        e.target.checked,
                      )
                    }
                  >
                    {t(i.name)}
                  </CustomCheckbox>
                ))}
              </Space>
            </Col>
          </Row>
          <Divider className="filter-divider" />
          <Row className="profile-data-row">
            <Col>
              <div className="filter-group-title">{t('STATUS')}</div>
              <Space
                direction="horizontal"
                wrap
                style={{ rowGap: '24px', marginBottom: '24px' }}
              >
                {statusList.map((i) => (
                  <CustomCheckbox
                    width="120px"
                    key={i.id}
                    checked={filterState?.status?.[i.label] || false}
                    onChange={(e) =>
                      handleFilterChange('status', i.label, e.target.checked)
                    }
                  >
                    {t(i.label)}
                  </CustomCheckbox>
                ))}
              </Space>
            </Col>
          </Row>
          <Divider className="filter-divider" />
          <div className="filter-group-title">{t('PERIOD')}</div>
          <Row className="profile-data-row" style={{ paddingBottom: '24px' }}>
            <Col span={12}>
              <DatePicker
                className="custom-datepicker"
                suffixIcon={<Picker />}
                placeholder={t('ENTER_START')}
                format="YYYY-MM-DD"
                onChange={(data) => handleChangeData(data, 'start')}
                value={filterState?.start}
              />
            </Col>
            <Col span={12}>
              <DatePicker
                className="custom-datepicker"
                suffixIcon={<Picker />}
                placeholder={t('ENTER_END')}
                format="YYYY-MM-DD"
                onChange={(data) => handleChangeData(data, 'end')}
                value={filterState?.end}
              />
            </Col>
          </Row>
          <Divider className="filter-divider" />
          <Row
            className="modal-newportfolio-button-wrapper"
            style={{ gap: '12px' }}
          >
            <Button
              type="text"
              className="modal-action-btn cancel-btn"
              onClick={handleReset}
            >
              {t('RESET')}
            </Button>
            <Button
              type="default"
              className="modal-action-btn save-btn"
              onClick={onSubmit}
            >
              {t('APPLY')}
            </Button>
          </Row>
        </div>
      </Form>
    </Modal>
  );
};

export default ModalFilterAdvise;

ModalFilterAdvise.propTypes = {
  filter: PropTypes.object,
  openFilter: PropTypes.bool,
  handleCloseFilter: PropTypes.func,
  setFilter: PropTypes.func,
  onboardingForms: PropTypes.array,
  onboardingTemplates: PropTypes.object,
};
